import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import { schoolApi } from '../../common/schoolApi';

/**
 * The schema migration runner, as a screen.
 *
 * WHY A SCREEN AND NOT JUST THE CLI
 * ---------------------------------
 * `php index.php migrate status` is still the authority and still what a deploy
 * runs. But the question it answers most often -- "is anything outstanding, and
 * on which schools?" -- gets asked far more than it gets acted on, and asking it
 * should not require SSH onto the API host. So this screen is read-mostly: a
 * status board first, with an Apply button on it.
 *
 * ONE SCHOOL PER REQUEST
 * ----------------------
 * The API will only migrate one database per call, and "Apply to all" here is a
 * loop, not a bulk endpoint. A fleet-wide run behind a web server is one request
 * that lasts minutes and dies at a proxy timeout with its log lost; looping
 * means a death costs one school and everything before it is already recorded in
 * the ledger. The progress bar is therefore honest -- it counts requests that
 * have actually come back.
 *
 * The loop STOPS at the first failure. Later migrations assume earlier ones
 * landed, and marching on through a fleet after one school has broken turns one
 * known-bad database into an unknown number of them.
 *
 * NO ROLLBACK BUTTON, AND NO FIX FOR DRIFT
 * ----------------------------------------
 * MySQL cannot roll back DDL, so there is nothing honest to put behind a Revert
 * button. DRIFT and ORPHAN rows mean the files and the ledger disagree about
 * history; they are shown in red with no button, because only a human knows
 * which side is right.
 *
 * @see api/application/controllers/api/Migration.php   the endpoints
 * @see api/application/controllers/Migrate.php         the same operations, from a terminal
 */

type Row = {
    version: number;
    file: string;
    state: string;
    note: string;
};

type Counts = {
    total: number;
    applied: number;
    pending: number;
    failed: number;
    blocked: number;
};

type Database = {
    db_name: string;
    school_id: number | null;
    school_name: string | null;
    version: number;
    rows: Row[];
    counts: Counts;
    runnable: boolean;
};

type Lane = {
    lane: 'master' | 'tenant';
    databases: Database[];
    summary: { databases: number; behind: number; blocked: number; pending: number };
};

type StatusResult = { lanes: Lane[]; enabled: boolean };

type RunResult = {
    applied: number;
    skipped: number;
    failed: number;
    seconds: number;
    log: string[];
};

/** What a migration row's state should look like. Unknown states stay neutral. */
const STATE_STYLE: Record<string, { color: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
    success: { color: 'success', label: 'applied' },
    pending: { color: 'default', label: 'pending' },
    running: { color: 'warning', label: 'interrupted' },
    failed: { color: 'error', label: 'failed' },
    DRIFT: { color: 'error', label: 'drift' },
    ORPHAN: { color: 'error', label: 'orphan' },
};

const StateChip = ({ state }: { state: string }) => {
    const s = STATE_STYLE[state] || { color: 'default' as const, label: state };
    return <Chip size="small" color={s.color} label={s.label} variant={s.color === 'default' ? 'outlined' : 'filled'} />;
};

/** The migration files of one database, folded away until asked for. */
const DatabaseRows = ({ rows }: { rows: Row[] }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 80 }}>Version</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell sx={{ width: 120 }}>State</TableCell>
                    <TableCell>Note</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(r => (
                    <TableRow key={`${r.version}-${r.file}`}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                            {String(r.version).padStart(4, '0')}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.file}</TableCell>
                        <TableCell>
                            <StateChip state={r.state} />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{r.note || '—'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

/** One database: the headline, the Apply button, and its files on demand. */
const DatabaseCard = ({
    db,
    busy,
    onApply,
}: {
    db: Database;
    busy: boolean;
    onApply: () => void;
}) => {
    const [open, setOpen] = useState(false);

    const title = db.school_id !== null
        ? `${db.school_name || 'School ' + db.school_id}`
        : 'Master';

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1">{title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {db.db_name}
                        {db.school_id !== null ? ` · school ${db.school_id}` : ''}
                        {` · at version ${String(db.version).padStart(4, '0')}`}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    {db.counts.blocked > 0 && (
                        <Chip size="small" color="error" label={`${db.counts.blocked} need a human`} />
                    )}
                    {db.counts.failed > 0 && (
                        <Chip size="small" color="error" variant="outlined" label={`${db.counts.failed} failed`} />
                    )}
                    {db.counts.pending > 0 ? (
                        <Chip size="small" color="warning" label={`${db.counts.pending} pending`} />
                    ) : (
                        db.counts.blocked === 0 && <Chip size="small" color="success" label="up to date" />
                    )}

                    <Button size="small" onClick={() => setOpen(o => !o)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                        {db.counts.total} file{db.counts.total === 1 ? '' : 's'}
                    </Button>

                    <Tooltip
                        title={
                            db.counts.blocked > 0
                                ? 'Drift or an orphaned entry has to be sorted out by hand first.'
                                : db.counts.pending === 0
                                ? 'Nothing outstanding.'
                                : 'Apply every pending migration to this database.'
                        }
                    >
                        <span>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!db.runnable || busy}
                                onClick={onApply}
                            >
                                {busy ? 'Running…' : 'Apply'}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Collapse in={open} unmountOnExit>
                <DatabaseRows rows={db.rows} />
            </Collapse>
        </Paper>
    );
};

/** The runner's own output, exactly as the library wrote it. */
const RunLog = ({ lines, error }: { lines: string[]; error: string | null }) => {
    if (!lines.length && !error) return null;
    return (
        <Box sx={{ mt: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    <AlertTitle>The run stopped</AlertTitle>
                    {error}
                </Alert>
            )}
            <Paper
                variant="outlined"
                sx={{
                    p: 1.5,
                    maxHeight: 320,
                    overflow: 'auto',
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '0.78rem',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {lines.join('\n')}
            </Paper>
        </Box>
    );
};

export const Migrations = () => {
    const notify = useNotify();

    const [status, setStatus] = useState<StatusResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [tab, setTab] = useState<'master' | 'tenant'>('master');

    // The database currently being migrated, as "lane:school_id", or null.
    const [busy, setBusy] = useState<string | null>(null);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [log, setLog] = useState<string[]>([]);
    const [runError, setRunError] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{ db: Database | null; lane: 'master' | 'tenant'; all: boolean } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await schoolApi.get<StatusResult>('migration/status');
            setStatus(res.result);
            setLoadError(null);
        } catch (e: any) {
            setStatus(null);
            setLoadError(e.message || 'Could not read the migration status.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const lane = useMemo(
        () => status?.lanes.find(l => l.lane === tab) || null,
        [status, tab],
    );

    const key = (l: string, db: Database) => `${l}:${db.school_id ?? 'master'}`;

    /**
     * One database, one request. Returns FALSE if it failed, so the fleet loop
     * knows to stop.
     */
    const runOne = async (l: 'master' | 'tenant', db: Database, append: boolean): Promise<boolean> => {
        setBusy(key(l, db));
        try {
            const res = await schoolApi.post<RunResult>('migration/run', {
                lane: l,
                school_id: db.school_id ?? undefined,
            });
            const header = `=== ${db.school_name || db.db_name}: ${res.result.applied} applied, ${res.result.skipped} already up to date, ${res.result.seconds}s ===`;
            setLog(prev => (append ? [...prev, ...res.result.log, header, ''] : [...res.result.log, header, '']));
            return true;
        } catch (e: any) {
            // The API sends the partial log WITH the error -- everything it
            // managed before it stopped, ending at the file that died. That is
            // the whole reason to look at this screen after a failure, so it is
            // shown even though the run did not succeed.
            const partial: string[] = (e.result && Array.isArray(e.result.log)) ? e.result.log : [];
            setRunError(e.message || 'The migration run failed.');
            setLog(prev => [
                ...(append ? prev : []),
                ...partial,
                `=== ${db.school_name || db.db_name}: FAILED ===`,
                e.message || '',
                '',
            ]);
            return false;
        } finally {
            setBusy(null);
        }
    };

    const applyOne = async (l: 'master' | 'tenant', db: Database) => {
        setRunError(null);
        const ok = await runOne(l, db, false);
        if (ok) notify('Migration finished.', { type: 'success' });
        await load();
    };

    /** Every school that is behind, one request at a time, stopping on failure. */
    const applyAll = async (l: 'master' | 'tenant', databases: Database[]) => {
        const todo = databases.filter(d => d.runnable);
        if (!todo.length) return;

        setRunError(null);
        setLog([]);
        setProgress({ done: 0, total: todo.length });

        for (let i = 0; i < todo.length; i++) {
            const ok = await runOne(l, todo[i], true);
            setProgress({ done: i + 1, total: todo.length });
            if (!ok) {
                notify(`Stopped after ${i} of ${todo.length}. Nothing further was attempted.`, { type: 'error' });
                break;
            }
        }

        setProgress(null);
        await load();
    };

    const confirmAndRun = async () => {
        const c = confirm;
        setConfirm(null);
        if (!c || !lane) return;
        if (c.all) {
            await applyAll(c.lane, lane.databases);
        } else if (c.db) {
            await applyOne(c.lane, c.db);
        }
    };

    // --- render ------------------------------------------------------------

    if (loading && !status) {
        return (
            <Card>
                <Title title="Database migrations" />
                <CardContent>
                    <CircularProgress size={24} />
                </CardContent>
            </Card>
        );
    }

    const runnableCount = lane ? lane.databases.filter(d => d.runnable).length : 0;

    return (
        <Card>
            <Title title="Database migrations" />
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Database migrations</Typography>
                    <Button startIcon={<RefreshIcon />} onClick={load} disabled={!!busy || loading}>
                        Refresh
                    </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The same runner as <code>php index.php migrate</code>, over the same ledger. There is no
                    undo: a migration that has to be reversed is run by hand from its <code>.rollback.sql</code>,
                    with a backup taken first.
                </Typography>

                {loadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loadError}
                    </Alert>
                )}

                {status && !status.enabled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Migrations are switched off on this server</AlertTitle>
                        Status still reads, but nothing can be applied until{' '}
                        <code>migration_enabled</code> is TRUE in{' '}
                        <code>application/config/migration.php</code>.
                    </Alert>
                )}

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    {(status?.lanes || []).map(l => (
                        <Tab
                            key={l.lane}
                            value={l.lane}
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>{l.lane === 'master' ? 'Master' : 'Schools'}</span>
                                    {l.summary.behind > 0 && (
                                        <Chip size="small" color="warning" label={l.summary.behind} />
                                    )}
                                    {l.summary.blocked > 0 && (
                                        <Chip size="small" color="error" label={l.summary.blocked} />
                                    )}
                                </Stack>
                            }
                        />
                    ))}
                </Tabs>

                {lane && (
                    <>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {lane.summary.databases} database{lane.summary.databases === 1 ? '' : 's'}
                                {lane.summary.behind > 0
                                    ? ` · ${lane.summary.behind} behind · ${lane.summary.pending} migration(s) outstanding`
                                    : ' · all up to date'}
                                {lane.summary.blocked > 0 ? ` · ${lane.summary.blocked} need a human` : ''}
                            </Typography>

                            {lane.lane === 'tenant' && (
                                <Button
                                    variant="contained"
                                    disabled={runnableCount === 0 || !!busy || !status?.enabled}
                                    onClick={() => setConfirm({ db: null, lane: 'tenant', all: true })}
                                >
                                    Apply to all {runnableCount || ''} behind
                                </Button>
                            )}
                        </Stack>

                        {progress && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {progress.done} of {progress.total} done
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(progress.done / progress.total) * 100}
                                />
                            </Box>
                        )}

                        {lane.databases.length === 0 && (
                            <Alert severity="info">
                                {lane.lane === 'tenant'
                                    ? 'No school databases are registered yet.'
                                    : 'No target database.'}
                            </Alert>
                        )}

                        {lane.databases.map(db => (
                            <DatabaseCard
                                key={db.db_name}
                                db={db}
                                busy={busy === key(lane.lane, db)}
                                onApply={() => setConfirm({ db, lane: lane.lane, all: false })}
                            />
                        ))}

                        <RunLog lines={log} error={runError} />
                    </>
                )}
            </CardContent>

            <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Apply migrations?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        {confirm?.all ? (
                            <>
                                This runs every outstanding migration against{' '}
                                <strong>{runnableCount} school database(s)</strong>, one at a time. It stops at
                                the first failure.
                            </>
                        ) : (
                            <>
                                This runs every outstanding migration against{' '}
                                <strong>{confirm?.db?.db_name}</strong>.
                            </>
                        )}
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Schema changes cannot be undone automatically. Take a backup first if this is
                            production.
                        </Alert>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={confirmAndRun}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default Migrations;
